<script>
/**
 * Overview
 * @version 1.0.0
 * @since 2.3.0
 */
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';

import { wizardListenerMixin } from '@/mixins/wizard';
import { LOADING_KEY, UNIT_TYPE_FRACTIONAL } from '@/util/constants';
import { getTotalAmountPriceCustom } from '@/util/utilsFinCalculator';
import { pluralize, formatAddressFormObject } from '@/util/utils';

import EzEntityInfo from '@/components/ui/EntityInfo';
import EzButton from '@/components/ui/Button';
import EzLoader from '@/components/ui/Loader/EzLoader';
import EzTable from '@/components/ui/Table';
import EzPremiumBadge from '@/components/ui/PremiumBadge';
import EzLiteBadge from '@/components/ui/LiteBadge';

import VEntityInfo from '@/components/v3/elements/VEntityInfo/index';
import VPrice from '@/components/v3/elements/VPrice';
import Paper from '@/components/layout/WizardOverviewPaper.vue';

import flash from '@/components/ui/FlashMessage';
import OverviewSidebar from './OverviewSidebar.vue';
import { TRANSFER_STEP_1, TRANSFER_STEP_2 } from './steps';

const Event = {
  setPercentType: 'setPercentType',
  setAmountType: 'setAmountType',
};

export default {
  mixins: [wizardListenerMixin],
  components: {
    EzEntityInfo,
    EzButton,
    EzLoader,
    EzTable,
    EzPremiumBadge,
    EzLiteBadge,
    VEntityInfo,
    VPrice,
    Paper,
    OverviewSidebar,
  },
  data() {
    return {
      productsListExpanded: false,
      Event,
      formatAddressFormObject,
    };
  },
  computed: {
    ...mapState('entities/orders', ['draft']),
    ...mapGetters('loading', ['isSomeLoading']),
    ...mapState('entities/users', ['loggedUser', 'contextId']),
    isLoading() {
      return this.isSomeLoading([
        LOADING_KEY.DISTRIBUTOR_CREATE_ORDER,
        LOADING_KEY.DISTRIBUTOR_ADD_INVOICE_TO_ORDER,
        LOADING_KEY.VENUE_INVENTORY_TRANSFER_PRICE_CHECK,
      ]);
    },
    venue() {
      return this.draft.venue;
    },
    venueId() {
      return this.contextId;
    },
    products() {
      return this.draft.products;
    },
    invoices() {
      return this.draft?.invoices || [];
    },
    totalAmount() {
      return getTotalAmountPriceCustom({ products: this.products });
    },
    listProducts() {
      if (!this.productsListExpanded) return this.products.slice(0, 8);
      return this.products;
    },
    expandButtonCopy() {
      return this.productsListExpanded ? 'Collapse List' : 'Expand List';
    },
    expandButtonIcon() {
      return this.productsListExpanded ? 'angle-up' : 'angle-down';
    },
    isSomeQuantityInvalid() {
      return this.products.some(p => p.quantity > p.orderedCount);
    },
  },
  methods: {
    ...mapMutations('entities/orders', [
      'SET_DRAFT_MESSAGE',
      'UPDATE_DRAFT',
      'UPDATE_DRAFT_PRODUCT',
      'CALCULATE_NEW_DRAFT_ORDER_AMOUNT',
      'UPDATE_DELIVERY_FEE_TO_NEW_ORDER',
    ]),
    ...mapActions('entities/orders', [
      'venueCreateInventoryTransfer',
      'venueInventoryTransferPriceCheck',
    ]),
    getRowClass(row) {
      return row.isBonus ? 'item-bonus' : null;
    },
    tradeDealTooltipContent(row) {
      return `Buy ${row.deals[0].buy} ${pluralize(row.orderingUnit.label, row.deals[0].buy)},
                    get ${row.deals[0].get} ${pluralize(
        row.orderingUnit.label,
        row.deals[0].get,
      )} for ${row.deals[0].price}${row.currency.symbol}`;
    },
    onPreviousStep() {
      this.$emit('stepBack');
    },
    isFractional(unit) {
      return unit?.type === UNIT_TYPE_FRACTIONAL;
    },
    totalPrice(product) {
      return product.price * product.quantity;
    },
    async onNextStep() {
      await this.venueCreateInventoryTransfer({
        venueId: this.venueId,
      });
      flash.success({ title: 'Inventory Transfer Completed' });

      const mediaQuery = window.matchMedia('(max-width: 768px)');
      if (mediaQuery.matches) {
        return this.$router.push({ name: 'platform-home' });
      }
      return this.$router.push({ name: 'venue-stock-all' });
    },
    editVenue() {
      this.$emit('stepBack', TRANSFER_STEP_1);
    },
    editProducts() {
      this.$emit('stepBack', TRANSFER_STEP_2);
    },
    toggleListExpanded() {
      this.productsListExpanded = !this.productsListExpanded;
    },
  },
  async created() {
    const { data } = await this.venueInventoryTransferPriceCheck({
      venueId: this.venueId,
      body: {
        items: this.products.map(p => ({
          id: p.id,
          quantity: p.quantity,
        })),
      },
    });
    this.products.forEach(product => {
      const priceCheck = data.data.find(item => item.id === product.id);
      if (priceCheck) {
        this.UPDATE_DRAFT_PRODUCT({
          ...product,
          price: priceCheck.price,
        });
      }
    });
    this.enableNextStep();
  },
  watch: {
    isSomeQuantityInvalid(val) {
      if (val) this.disableNextStep();
      else this.enableNextStep();
    },
  },
};
</script>

<template>
  <div class="create-order">
    <div class="create-order__main-content">
      <paper fullWidth class="venue-section">
        <template #actions>
          <ez-button type="link" formType="button" @click="editVenue">
            <font-awesome-icon icon="pen" />
            <span>Edit</span>
          </ez-button>
        </template>
        <div class="ez-info-panel">
          <v-entity-info
            :imageUrl="venue.logo"
            imageSize="64px"
            imageBorderRadius="50%"
            imageHasBorder
          >
            <template>
              <span>
                {{ venue.name }}
              </span>
            </template>
            <template #suffix>
              <ez-premium-badge v-if="$helpers.isPremium(venue.accountType)" />
              <ez-lite-badge v-if="$helpers.isLite(venue.accountType)" />
            </template>
          </v-entity-info>
        </div>
      </paper>

      <paper fullWidth>
        <template #title>Products ({{ products.length }})</template>
        <template #actions>
          <ez-button type="link" formType="button" @click="editProducts">
            <font-awesome-icon icon="pen" />
            <span>Edit</span>
          </ez-button>
        </template>
        <div class="products-table">
          <ez-table
            :data="listProducts"
            :columns="['product', 'distributor', 'quantity', 'price', 'total']"
            :columnProps="{
              product: { class: 'product-cell' },
              quantity: { class: 'quantity-cell' },
              price: { class: 'pricePerUnit-cell' },
              total: { class: 'total-cell no-padding-cell' },
            }"
            :headers="{
              product: () => 'Product',
              distributor: () => 'Supplier',
              price: () => 'Price per Unit',
              total: () => 'Total Price',
            }"
            :rowCls="getRowClass"
          >
            <template #cell-product="{ row }">
              <ez-entity-info imgWidth="28px" imgHeight="28px" :imgUrl="row.image">
                <div class="product-info" :title="row.name">
                  <span>
                    {{ row.name }}
                  </span>
                  <span class="product-info-secondary">
                    <span class="product-info__unit">{{ row.orderingUnit.label }}</span>
                    <template v-if="row.sku">
                      &#8226;
                      <span class="product-info__sku">{{ row.sku }}</span>
                    </template>
                  </span>
                </div>
              </ez-entity-info>
            </template>

            <template #cell-distributor="{ row: { distributor } }">
              <ez-entity-info
                v-if="distributor"
                imgWidth="2.125rem"
                imgHeight="2.125rem"
                :imgUrl="distributor.logo"
                imgBorderRadius="50%"
              >
                <div class="distributor-info" :title="distributor.name">
                  {{ distributor.name }}
                </div>
              </ez-entity-info>
            </template>

            <template #cell-quantity="{ row: { quantity } }">
              <span>{{ quantity }}</span>
            </template>

            <template #cell-price="{ row }">
              <v-price
                :price="row.price"
                :currency="row.currency"
                :showMarketPriceInfo="false"
                :isMarketPrice="row.marketPrice"
                flexRow
              />
            </template>

            <template #cell-total="{ row }">
              <v-price
                :price="totalPrice(row)"
                :currency="row.currency"
                :showMarketPriceInfo="false"
              />
            </template>
          </ez-table>
        </div>
        <div class="products-table--mobile">
          <div
            v-for="product in listProducts"
            :key="product.id"
            class="product-item"
            :id="`product-item-${product.id}`"
          >
            <div class="first-row">
              <ez-entity-info imgWidth="2.5rem" imgHeight="2.5rem" :imgUrl="product.image">
                <div class="product-info mr-8" :title="product.name">
                  <span>
                    {{ product.name }}
                  </span>
                  <span class="product-info-secondary">
                    <span class="product-info-secondary--category category-badge">
                      {{ product.category | categoryWithParent }}
                    </span>
                  </span>
                </div>
              </ez-entity-info>
              <div class="quantity-input">
                <span>{{ product.quantity }}</span>
              </div>
            </div>
            <hr />
            <div class="second-row">
              <v-price
                :price="product.price"
                :currency="product.currency"
                :showMarketPriceInfo="false"
                :isMarketPrice="product.marketPrice"
                :unit="product?.priceUnit?.label || product?.orderingUnit?.label"
                flexRow
              />
              <v-price
                :price="totalPrice(product)"
                :currency="product.currency"
                :showMarketPriceInfo="false"
              />
            </div>
          </div>
        </div>
        <template #footer>
          <div v-if="products.length > 8" class="u-flex-h-center mt-16">
            <ez-button @click="toggleListExpanded" type="link" formType="button">
              <span>{{ expandButtonCopy }}</span>
              <font-awesome-icon class="ml-8" :icon="expandButtonIcon" />
            </ez-button>
          </div>
        </template>
      </paper>
    </div>

    <overview-sidebar />

    <ez-loader :show="isLoading">Loading...</ez-loader>
  </div>
</template>

<style scoped lang="scss">
.create-order {
  width: 100%;
  display: flex;
  @media (max-width: 768px) {
    flex-direction: column;
  }
}

.venue-section :deep() {
  .overview-paper__content {
    display: flex;
    padding: 0 24px;
    padding-bottom: 12px;
  }

  .entity-info {
    flex-direction: column;
    align-items: center;
    margin-right: 24px;

    &__info {
      justify-content: center;
      margin-left: 0;
      margin-top: 12px;
      @include font-size(16px, 22px);
      font-weight: 600;
      color: $color-gray-25;
    }
  }

  .ref-number {
    &__label {
      @include font-size(12px, 18px);
      color: $color-gray-6C;
    }

    &__value {
      @include font-size(14px, 20px);
      color: $color-gray-25;
    }
  }
}

.products-table {
  .quantity-cell {
    width: 64px + $spacing-32;

    .quantity-input {
      :deep() .input-group {
        input {
          color: $color-primary-blue;
          font-weight: bold;
        }

        button {
          display: none;
        }
      }
    }
  }

  .pricePerUnit-cell {
    width: 90px + $spacing-32;

    :deep() .v-price__price {
      font-weight: bold;

      &:not(.v-price__market-price--row, .v-price__unit--row) {
        color: $color-gray-25;
      }
    }
  }

  .discount-cell {
    width: 95px + $spacing-32;
    overflow: visible;

    :deep() {
      .mask-input {
        &__input {
          @include font-size(16px, 20px);
        }

        &__prefix {
          color: $color-gray-6C;
          font-weight: bold;
        }
      }

      .mark-input__error {
        display: none;
      }
    }
  }

  .total-cell {
    width: 125px;
    overflow: visible;

    :deep() span {
      font-weight: bold;
      color: $color-gray-25;
    }
  }

  .product-cell {
    .product-info {
      display: flex;
      flex-direction: column;

      span {
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
      }

      &__unit {
        color: $color-gray-25;
      }
    }
  }
  tbody tr.item-bonus {
    background-color: $color-pastel-blue;
    td:first-child {
      padding-left: 8px;
    }
    td:last-child {
      padding-right: 8px;
    }
    :deep() .has-border {
      border: 1px solid $color-primary-blue;
    }
    .additional-info--bonus {
      color: $color-gray-6C;
      font-size: 12px;
      font-weight: 500;
      line-height: 14px;
      border-bottom: 2px dotted #a3bbff;
      color: $color-primary-blue;
    }
  }
}

.note-options {
  .note-option {
    &__label {
      @include font-size(12px, 22px);
      font-weight: 600;
      color: $color-gray-25;
    }
  }
}

.info-icon {
  width: $spacing-16;
  height: $spacing-16;
}

:deep() .loader {
  @extend %whole-screen;
}

:deep() .mask-input-wrapper {
  .mask-input__input {
    text-align: center;
  }
}

.ez-info-panel {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: -28px;
  align-items: center;
  &__title {
    @extend %flex-center;
    margin-bottom: 1.25rem;
    h3 {
      @include font-size(20px);
      font-weight: 500;
      margin: 0;
    }
  }
  &__item {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    small {
      @extend %gray-label;
      font-weight: 500;
      margin-bottom: 0.5rem;
    }
    span {
      @include font-size(14px);
      white-space: pre-line;
    }
    a {
      color: #4d7cfe;
    }
  }
  &__contact {
    display: flex;
    margin-top: 1rem;

    &:not(:last-child) {
      padding-bottom: 0.5rem;
    }
  }
  &__close-button {
    margin-left: auto;
    cursor: pointer;
    svg {
      fill: $color-gray-6C;
    }
  }
  .venue-info {
    margin-top: -16px;
  }
}

.products-table--mobile {
  display: none;
  padding-bottom: 32px;
  width: 100%;

  .product-item {
    padding: 12px 0;
    border-bottom: 1px solid #e1e5ed;
    hr {
      margin: 12px 0;
    }
    &:hover {
      cursor: pointer;
    }
  }

  .first-row {
    display: flex;
    align-items: center;
    line-height: 1.5;
    justify-content: space-between;

    .entity-info {
      align-items: center;
      min-width: 0;
      flex-shrink: 1;
    }

    .product-quantity {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      color: $color-gray-6C;
      font-size: 14px;
      flex-shrink: 0;

      .product-info-secondary--category {
        font-size: 12px;
      }
    }
  }

  .second-row {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .quantity__icon {
      padding-left: 4px;
    }

    .quantity-input {
      display: flex;
      align-items: center;

      :deep() .mask-input-wrapper {
        max-width: 100px;
      }

      :deep() .mask-input-wrapper .mask-input .mask-input__input {
        border-radius: 0;
        font-size: 16px;
      }
    }
    .quantity .product-card-cart {
      display: flex;
      align-items: center;
      width: 120px;
      margin-right: 12px;

      :deep() .mask-input-wrapper {
        margin: 0 8px;
      }

      :deep() .mask-input-wrapper .mask-input .mask-input__input {
        border-radius: 0;
        font-size: 16px;
      }
    }
  }

  .product-info {
    display: flex;
    flex-direction: column;
    line-height: 1.5;
    font-size: 14px;
    font-weight: 500;

    .product-info-secondary {
      display: block;
      font-size: 12px;
    }

    span {
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }
}

@media (max-width: 768px) {
  .products-table--mobile {
    display: block;
  }
  :deep() .products-table {
    display: none;
  }
}
</style>
